import React, { useRef, useEffect } from "react";
import { Input } from "@chakra-ui/react";

const TEMPLATE = "___-__-____";
const SLOT_INDEXES = [0, 1, 2, 4, 5, 7, 8, 9, 10];

const clampDigits = (raw) => String(raw || "").replace(/\D/g, "").slice(0, 9);

const formatWithMask = (digits) => {
  const d = clampDigits(digits);
  const chars = TEMPLATE.split("");
  for (let i = 0; i < d.length; i++) chars[SLOT_INDEXES[i]] = d[i];
  return chars.join("");
};

const caretAtFirstEmpty = (formatted) => {
  const i = formatted.indexOf("_");
  return i === -1 ? formatted.length : i;
};

/**
 * SSNMaskedInput
 * Takes in raw digits as value and calls onChange with raw digits.
 * Renders an input masked as a Social Security Number XXX-XX-XXXX.
 * Doesn't hold any local state, fully controlled.
*/

function SSNMaskedInput({
    id,
    name,
    ariaLabel = "Social Security Number",
    value = "", // raw digits
    onChange,
    onBlur,
    disabled,
}) {
  const ref = useRef(null);
  const formatted = formatWithMask(value);

  const moveCaret = () => {
    const node = ref.current;
    if (!node) return;
    const pos = caretAtFirstEmpty(formatted);
    requestAnimationFrame(() => node.setSelectionRange(pos, pos));
  };

  useEffect(() => {
    moveCaret(); // sync caret when Formik changes value programmatically
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const forceFocusToSlot = (e) => {
    e.preventDefault();     // stop default caret placement
    e.stopPropagation();
    ref.current?.focus();
    moveCaret();
  };

  const handleChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const raw = clampDigits(e.target.value);
    onChange?.(raw);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const txt = e.clipboardData?.getData?.("text");
    if (txt) {
      const digits = clampDigits(txt);
      onChange?.(digits);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      // Remove the previous filled digit no matter what character we're “on”
      e.preventDefault();
      e.stopPropagation();
      if (value.length > 0) onChange?.(value.slice(0, value.length - 1));
      return;
    }
    // Let all other keys flow; non-digits will be filtered in onChange
  };

  return (
    <Input
        id={id}
        name={name}
        ref={ref}
        value={formatted}
        aria-label={ariaLabel}
        placeholder={TEMPLATE}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={forceFocusToSlot}
        onBlur={onBlur}
        disabled={disabled}
        onClick={forceFocusToSlot}
        onKeyDown={handleKeyDown}
    />
  );
}

export default SSNMaskedInput;
